// apps/admin/src/hooks/useSavedViews.ts
//
// Phase 8.1 Wave 2 Task 07 — Saved Views.
//
// vg-binding: CONTEXT:decisions=P8.D-287,P8.D-288,P8.D-289
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: API-CONTRACTS:POST_/api/v1/admin/saved-views
//
// Saved Views are server-backed (per-user) with a localStorage cache for
// instant render. List + create + delete via TanStack Query. The hook is
// table-agnostic — caller supplies `tableKey` (e.g. "orders.list").
//
// API contract (per phase 8.1 API-CONTRACTS — POST /api/v1/admin/saved-views
// + GET/DELETE companions). All responses follow INTERFACE-STANDARDS.md
// envelope `{ ok, data, error }`. Toast on error uses
// `error.user_message ?? error.message`.

import { useCallback, useEffect, useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { DataTableState } from './useDataTableState.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface SavedView {
  id: string;
  tableKey: string;
  name: string;
  /** Snapshot of DataTableState that should restore on apply. */
  state: Pick<DataTableState, 'filters' | 'sort' | 'columnVisibility' | 'pageSize'>;
  createdAt: string;
}

export interface UseSavedViewsOptions {
  tableKey: string;
  /** Disable the network round-trip — localStorage-only. */
  localOnly?: boolean;
}

export interface UseSavedViewsApi {
  views: SavedView[];
  isLoading: boolean;
  error: unknown;
  list: UseQueryResult<SavedView[], unknown>;
  create: UseMutationResult<
    SavedView,
    unknown,
    { name: string; state: SavedView['state'] }
  >;
  remove: UseMutationResult<void, unknown, { id: string }>;
}

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const API_BASE_URL =
  ((import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env
    ?.VITE_API_BASE_URL ?? '/api/v1');

function cacheKey(tableKey: string): string {
  return `admin_saved_views_${tableKey}`;
}

function readCache(tableKey: string): SavedView[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(cacheKey(tableKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is SavedView =>
        v != null &&
        typeof v === 'object' &&
        typeof (v as SavedView).id === 'string' &&
        typeof (v as SavedView).name === 'string',
    );
  } catch {
    return [];
  }
}

function writeCache(tableKey: string, views: SavedView[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(cacheKey(tableKey), JSON.stringify(views));
  } catch {
    // quota / disabled — silently ignore
  }
}

// ---------------------------------------------------------------------------
// API client (interface-standards envelope)
// ---------------------------------------------------------------------------

interface ApiEnvelope<T> {
  ok: boolean;
  data?: T;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    field_errors?: Record<string, string[]>;
  };
}

class SavedViewError extends Error {
  code: string | undefined;
  userMessage: string | undefined;
  constructor(message: string, code?: string, userMessage?: string) {
    super(message);
    this.name = 'SavedViewError';
    this.code = code;
    this.userMessage = userMessage;
  }
}

/**
 * Read the non-HttpOnly `XSRF-TOKEN` cookie for the CSRF double-submit header.
 * This helper uses raw fetch (not the canonical apiClient) so it must attach
 * `X-XSRF-TOKEN` itself on mutating verbs — otherwise the server rejects
 * save/delete with "CSRF token missing" (debug 2026-07-14).
 */
function readXsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

async function apiRequest<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // CSRF double-submit — mutating verbs echo the XSRF-TOKEN cookie.
  if (method !== 'GET') {
    const xsrf = readXsrfCookie();
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  }
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new SavedViewError(
      'Network error - check connection',
      'NETWORK_ERROR',
      undefined,
    );
  }
  let envelope: ApiEnvelope<T> | null;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    envelope = null;
  }
  if (!res.ok || !envelope?.ok) {
    const code = envelope?.error?.code ?? `HTTP_${res.status}`;
    const message =
      envelope?.error?.user_message ??
      envelope?.error?.message ??
      'Saved view request failed';
    throw new SavedViewError(message, code, envelope?.error?.user_message);
  }
  // GET/POST return `data`; DELETE returns `ok:true` with no data.
  return envelope.data as T;
}

function listEndpoint(tableKey: string): string {
  const qs = new URLSearchParams({ tableKey }).toString();
  return `/admin/saved-views?${qs}`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSavedViews(
  options: UseSavedViewsOptions,
): UseSavedViewsApi {
  const { tableKey, localOnly = false } = options;
  const qc = useQueryClient();

  const queryKey = useMemo(() => ['admin', 'saved-views', tableKey] as const, [tableKey]);

  const list = useQuery<SavedView[], unknown>({
    queryKey,
    queryFn: async () => {
      if (localOnly) return readCache(tableKey);
      try {
        const data = await apiRequest<SavedView[]>('GET', listEndpoint(tableKey));
        const safe = Array.isArray(data) ? data : [];
        writeCache(tableKey, safe);
        return safe;
      } catch (e) {
        // Fallback to cache so the UI still renders something.
        const cached = readCache(tableKey);
        if (cached.length > 0) return cached;
        throw e;
      }
    },
    staleTime: 30_000,
    initialData: () => readCache(tableKey),
  });

  useEffect(() => {
    if (list.data && Array.isArray(list.data)) {
      writeCache(tableKey, list.data);
    }
  }, [list.data, tableKey]);

  const create = useMutation<
    SavedView,
    unknown,
    { name: string; state: SavedView['state'] }
  >({
    mutationFn: async ({ name, state }) => {
      if (localOnly) {
        const view: SavedView = {
          id: `local-${Date.now()}`,
          tableKey,
          name,
          state,
          createdAt: new Date().toISOString(),
        };
        return view;
      }
      const data = await apiRequest<SavedView>(
        'POST',
        listEndpoint(tableKey),
        { name, state },
      );
      return data;
    },
    onSuccess: (view) => {
      const current = qc.getQueryData<SavedView[]>(queryKey) ?? [];
      const next = [...current.filter((v) => v.id !== view.id), view];
      qc.setQueryData(queryKey, next);
      writeCache(tableKey, next);
    },
  });

  const remove = useMutation<void, unknown, { id: string }>({
    mutationFn: async ({ id }) => {
      if (!localOnly) {
        await apiRequest<null>('DELETE', `/admin/saved-views/${encodeURIComponent(id)}`);
      }
    },
    onSuccess: (_void, { id }) => {
      const current = qc.getQueryData<SavedView[]>(queryKey) ?? [];
      const next = current.filter((v) => v.id !== id);
      qc.setQueryData(queryKey, next);
      writeCache(tableKey, next);
    },
  });

  const views = useMemo(() => list.data ?? [], [list.data]);

  return useMemo<UseSavedViewsApi>(
    () => ({
      views,
      isLoading: list.isLoading,
      error: list.error,
      list,
      create,
      remove,
    }),
    [views, list, create, remove],
  );
}

// Re-export so callers can `catch (e)` narrowly.
export { SavedViewError };

// Helper consumed by Phase 8.1 modules to map an API error → toast string per
// Interface Standards priority order.
export function savedViewErrorMessage(err: unknown): string {
  if (err instanceof SavedViewError) {
    return err.userMessage ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Saved view request failed';
}

/**
 * Helper for callers — apply a saved view to a DataTableState replacer.
 * Pulls only the subset we persist (filters/sort/columnVisibility/pageSize),
 * leaving page=1 to honour list-restart semantics.
 */
export function projectSavedViewToState(view: SavedView): Partial<DataTableState> {
  return {
    filters: view.state.filters,
    sort: view.state.sort,
    columnVisibility: view.state.columnVisibility,
    pageSize: view.state.pageSize,
    page: 1,
  };
}

// Convenience: callback for FilterBar "Clear" affordance.
export function useClearSavedViewCache(tableKey: string): () => void {
  return useCallback(() => writeCache(tableKey, []), [tableKey]);
}
